import React, { useCallback } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Panel } from '../../components/primitives/Panel';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { formatPlanPrice, useDisplayCurrency } from '../../config/useDisplayCurrency';
import { useNextPlanForFeature } from '../../hooks/useNextPlanForFeature';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingPlanLockCardProps {
  feature: SMLUpgradeFeature.earnings | SMLUpgradeFeature.streamingSplit;
  /** The result first: what the plan shows ("See what each store pays you"). */
  title: MessageDescriptor;
  testId: string;
}

/**
 * The card in place of a part of Streaming that the plan does not show (earnings on Free, the
 * split by store and country on Free). The result first, then the plan and its price ("On Artist,
 * $19 a month."). The button opens the upgrade prompt. Inside a native app's web view the card
 * names the plan with no price (App Store 3.1.1).
 */
const StreamingPlanLockCardComponent: React.FC<StreamingPlanLockCardProps> = ({ feature, title, testId }) => {
  const intl = useIntl();
  const { nextPlan } = useNextPlanForFeature(feature);
  const currency = useDisplayCurrency();
  const isNativeEmbed = useIsNativeEmbed();
  const openUpgradePrompt = useUpgradePrompt();
  const upgrade = useCallback(() => openUpgradePrompt(feature), [feature, openUpgradePrompt]);

  const planLine =
    nextPlan === null
      ? null
      : isNativeEmbed
        ? intl.formatMessage(messages.planLineNoPrice, { plan: intl.formatMessage(planNameMessages[nextPlan.tier]) })
        : intl.formatMessage(messages.planLine, {
            plan: intl.formatMessage(planNameMessages[nextPlan.tier]),
            price: formatPlanPrice(intl, nextPlan.prices.monthly.amount, currency),
          });

  return (
    <Panel nested className={styles.card} data-testid={testId}>
      <span className={styles.lockChip}>
        <FontAwesomeIcon icon={faLock} />
      </span>
      <h3>{intl.formatMessage(title)}</h3>
      {planLine === null ? null : <p>{planLine}</p>}
      <SMLButton size="sm" color="primary" omitCap onClick={upgrade}>
        {intl.formatMessage(messages.planUpgrade)}
      </SMLButton>
    </Panel>
  );
};

export const StreamingPlanLockCard = TypedMemo(StreamingPlanLockCardComponent);
